import { themeGet } from '@styled-system/theme-get'
import React, { forwardRef, useImperativeHandle, useState, Ref, createRef } from 'react'
import styled from 'styled-components'
import {
  border, color, compose, space
} from 'styled-system'
import { ChevronDown } from '@zuto/icons'
import { textStyle, useDidMountEffect } from '@zuto/utilities'
import { colors } from '@zuto/theme/src/zuto/core'
import { textStyles } from '@zuto/theme/src/zuto/typography'

export interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string,
  toggleChanged?: (state: boolean) => void,
  defaultState?: boolean
}

type AccordionHandle = {
  toggleAccordion: () => void,
}

const AccordionWrapper = styled.div`
  width: 100%;
  ${(props) => compose(border, color)({
    ...props,
    ...themeGet('accordion.wrapper')(props)
  })}
`

const AccordionToggle = styled.button`
  ${(props) => compose(space)({
    ...props,
    ...themeGet('accordion.button')(props)
  })}
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  display: flex;
  svg {
    transition: transform 0.6s ease-in-out;
    transform: rotate(0deg);
    align-self: center;
  }
  &.extendableRadiosSideBySide {
    cursor: pointer;
  }
  &.open {
    ${themeGet('accordion.button.open')}
    svg {
      transform: rotate(-180deg);
    }
  }
`

const AccordionTitle = styled.div`
  flex: 1;
  &.extendableRadiosSideBySide {
    color: ${colors.neutrals[3]};
    ${textStyles.body.regular.normal};
    &:hover {
      color: ${colors.secondary[2]}
    }
  }
  ${(props) => compose(space, textStyle(props), color)({
    ...props,
    ...themeGet('accordion.title')(props)
  })}
`

const AccordionContent = styled.div`
  transition: max-height 0.6s ease-in-out, opacity 0.6s ease-in-out;
  max-height: 0;
  opacity: 0;
  margin-bottom: 16px;

  &.extendableRadiosSideBySide {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  &.open {
    max-height: 1000px;
    opacity: 1;
  }
`

const Accordion = forwardRef(({
  title, children, toggleChanged, defaultState = false, ...props
}: AccordionProps, ref: Ref<AccordionHandle>) => {
  const [open, setOpen] = useState(defaultState)
  const accordionContentRef = createRef<HTMLDivElement>()
  // The closed content is clipped with no margin. After mount, toggle() and onTransitionEnd keep these in step with
  // the transition, so they are only set here for the first render.
  const [initialContentStyle] = useState(() => (defaultState ? undefined : { overflow: 'hidden', margin: 0 }))

  const toggle = (newState?: boolean) => {
    if (accordionContentRef.current && !open) {
      accordionContentRef.current.style.removeProperty('overflow')
      accordionContentRef.current.style.removeProperty('margin')
    }
    setOpen(newState || !open)
  }

  useImperativeHandle(ref, () => ({
    toggleAccordion() {
      toggle(true)
    }
  }))

  useDidMountEffect(() => {
    if (toggleChanged) {
      toggleChanged(open)
    }
  }, [open])

  const onTransitionEnd = () => {
    if (accordionContentRef.current && !open) {
      accordionContentRef.current.style.overflow = 'hidden'
      accordionContentRef.current.style.margin = '0'
    }
  }

  return (
    <AccordionWrapper {...props}>
      <AccordionToggle onClick={() => toggle()} className={['extendableRadiosSideBySide', open ? 'open' : ''].join(' , ')} data-testid="accordion-button">
        <AccordionTitle className="extendableRadiosSideBySide">{title}</AccordionTitle>
        <ChevronDown />
      </AccordionToggle>
      <AccordionContent
        ref={accordionContentRef}
        onTransitionEnd={onTransitionEnd}
        className={['extendableRadiosSideBySide', open ? 'open' : ''].join(' , ')}
        style={initialContentStyle}
        data-testid="accordion-content"
      >
        {children}
      </AccordionContent>
    </AccordionWrapper>
  )
})

export default Accordion
